import React, { useState, useEffect, useRef } from 'react';
import Navbar from '../../components/invnavbar/invnavbar';
import Sidebar from '../../components/invsidebar/invsidebar';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Badge } from 'primereact/badge';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
    API_URLS,
    obtenerProductos,
    obtenerUsuarios
} from '../../Services/servicioURL';

interface InventarioItem {
    cve_producto: string;
    descripcion: string;
    modelo: string;
}

interface Usuario {
    id: number;
    nombre: string;
}

interface Sucursal {
    id: string;
    nombre: string;
}

interface Departamento {
    cve: string;
    nombre: string;
}

interface MovimientoDetalle {
    folio: string;
    fecha: string;
    cve_producto?: string;
    cve_producto_principal?: string;
    cantidad: number;
    observaciones?: string;
    referencia?: string;
    sucursal_destino?: string;
    id_sucursal_destino?: string | number;
    cve_departamento_destino?: string;
    nombre_usuario?: string;
}

const Saldos: React.FC = () => {
    const [productosCatalogo, setProductosCatalogo] = useState<InventarioItem[]>([]);
    const [usuariosCatalogo, setUsuariosCatalogo] = useState<Usuario[]>([]);
    const [sucursalesCatalogo, setSucursalesCatalogo] = useState<Sucursal[]>([]);
    const [departamentosCatalogo, setDepartamentosCatalogo] = useState<Departamento[]>([]);
    const [selectedProducto, setSelectedProducto] = useState<InventarioItem | null>(null);
    const [globalFilterValue, setGlobalFilterValue] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [productoEntradas, setProductoEntradas] = useState<MovimientoDetalle[]>([]);
    const [productoSalidas, setProductoSalidas] = useState<MovimientoDetalle[]>([]);
    const [productoMantenimientos, setProductoMantenimientos] = useState<any[]>([]);
    const [totalEntradas, setTotalEntradas] = useState<number>(0);
    const [totalSalidas, setTotalSalidas] = useState<number>(0);
    const [totalMantenimientos, setTotalMantenimientos] = useState<number>(0);
    const [saldoFinal, setSaldoFinal] = useState<number>(0);
    const [productoEntradasFull, setProductoEntradasFull] = useState<any[]>([]);
    const [productoSalidasFull, setProductoSalidasFull] = useState<any[]>([]);
    const [productoMantenimientosFull, setProductoMantenimientosFull] = useState<any[]>([]);
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [selectedMovimiento, setSelectedMovimiento] = useState<any>(null);
    const [movimientoType, setMovimientoType] = useState<'entrada' | 'salida' | 'mantenimiento'>('entrada');
    const toast = useRef<Toast>(null);
    
    // Ref para la sección de resultados
    const resultadosRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const fetchCatalogos = async () => {
            try {
                const [dataProductos, dataUsuarios, dataSucursales, dataDepartamentos] = await Promise.all([
                    obtenerProductos(API_URLS.productos.obtener),
                    obtenerUsuarios(API_URLS.usuarios.obtener),
                    fetch(API_URLS.sucursales.obtener).then(res => res.json()),
                    fetch(API_URLS.departamentos.obtener).then(res => res.json())
                ]);
                
                const inventarioData = dataProductos.map((producto: any) => ({
                    cve_producto: producto.cve,
                    descripcion: producto.descripcion,
                    modelo: producto.modelo
                }));
                setProductosCatalogo(inventarioData);
                
                const usuariosData = dataUsuarios.map((usuario: any) => ({
                    id: usuario.id,
                    nombre: usuario.nombre
                }));
                setUsuariosCatalogo(usuariosData);
                
                // Suponiendo que el backend regresa [{id, nombre}, ...]
                setSucursalesCatalogo(dataSucursales.map((s: any) => ({
                    id: String(s.id),
                    nombre: s.nombre
                })));

                // Cargar departamentos
                setDepartamentosCatalogo(dataDepartamentos.map((d: any) => ({
                    cve: d.cve,
                    nombre: d.nombre
                })));
                
            } catch (error) {
                console.error('Error al cargar catálogos:', error);
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'No se pudieron cargar los catálogos',
                    life: 3000
                });
            }
        };
        fetchCatalogos();
    }, []);

    const formatDate = (dateString?: string): string => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return isNaN(date.getTime()) ? '-' : date.toISOString().split('T')[0];
    };

    const getDescripcionProducto = (cve_producto: string): string => {
        const producto = productosCatalogo.find(p => p.cve_producto === cve_producto);
        return producto ? producto.descripcion : 'Limpieza/Mantenimiento';
    };

    const getNombreTecnico = (id_tecnico: number): string => {
        const tecnico = usuariosCatalogo.find(u => u.id === id_tecnico);
        return tecnico ? tecnico.nombre : 'Técnico no encontrado';
    };

    const getNombreSucursal = (id: string | number): string => {
        const suc = sucursalesCatalogo.find(s => s.id === String(id));
        return suc ? suc.nombre : (id ? String(id) : '-');
    };

    const getNombreDepartamento = (cve: string): string => {
        const dept = departamentosCatalogo.find(d => d.cve === cve);
        return dept ? dept.nombre : (cve || '-');
    };

    const isMovimientoDetalle = (data: any): data is MovimientoDetalle => {
        return (
            typeof data === 'object' &&
            data !== null &&
            'folio' in data &&
            'fecha' in data &&
            ('cve_producto' in data || 'cve_producto_principal' in data) &&
            'cantidad' in data
        );
    };

    const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setGlobalFilterValue(e.target.value);
    };

    // Función para hacer scroll automático a los resultados
    const scrollToResultados = () => {
        setTimeout(() => {
            if (resultadosRef.current) {
                resultadosRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });
            }
        }, 500); // Pequeño delay para que se carguen los datos primero
    };

    const onProductoSelect = (producto: InventarioItem) => {
        setSelectedProducto(producto);
        toast.current?.show({
            severity: 'info',
            summary: 'Producto seleccionado',
            detail: `Cargando movimientos para: ${producto.descripcion}`,
            life: 2000
        });
        
        // Hacer scroll automático a los resultados
        scrollToResultados();
    };

    const productRowClass = (data: InventarioItem) => {
        return selectedProducto && selectedProducto.cve_producto === data.cve_producto 
            ? 'p-highlight' 
            : '';
    };

    useEffect(() => {
        const fetchMovimientos = async () => {
            if (!selectedProducto || !selectedProducto.cve_producto) {
                setProductoEntradas([]);
                setProductoSalidas([]);
                setProductoMantenimientos([]);
                setTotalEntradas(0);
                setTotalSalidas(0);
                setTotalMantenimientos(0);
                setSaldoFinal(0);
                setError(null);
                setProductoEntradasFull([]);
                setProductoSalidasFull([]);
                setProductoMantenimientosFull([]);
                return;
            }

            const cve_producto = selectedProducto.cve_producto;
            setLoading(true);
            setError(null);

            try {
                const urlEntradas = `${API_URLS.entradas.obtener}?producto=${cve_producto}`;
                const urlSalidas = `${API_URLS.salidas.obtener}?producto=${cve_producto}`;
                const urlMantenimientos = `${API_URLS.mantenimientos.obtener}?producto=${cve_producto}`;

                const [resEntradas, resSalidas, resMantenimientos] = await Promise.all([
                    fetch(urlEntradas),
                    fetch(urlSalidas),
                    fetch(urlMantenimientos)
                ]);

                if (!resEntradas.ok || !resSalidas.ok || !resMantenimientos.ok) {
                    throw new Error('Error al obtener movimientos');
                }

                const dataEntradas = await resEntradas.json();
                const dataSalidas = await resSalidas.json();
                const dataMantenimientos = await resMantenimientos.json();

                setProductoEntradasFull(dataEntradas);
                setProductoSalidasFull(dataSalidas);
                setProductoMantenimientosFull(dataMantenimientos);

                const entradasProcesadas = dataEntradas.flatMap((e: any) =>
                    Array.isArray(e.entradaDetalle) ? e.entradaDetalle.map((detalle: any) => ({
                        ...detalle,
                        fecha: e.fecha,
                        observaciones: e.observaciones || '-',
                        referencia: e.referencia || '-',
                        folio: e.folio
                    })) : []
                );

                const salidasProcesadas = dataSalidas.flatMap((s: any) =>
                    Array.isArray(s.salidaDetalle) ? s.salidaDetalle.map((detalle: any) => ({
                        ...detalle,
                        fecha: s.fecha,
                        observaciones: s.observaciones || '-',
                        folio: s.folio,
                        sucursal_destino: detalle.id_sucursal_destino || '-',
                        departamento_destino: detalle.cve_departamento_destino || '-',
                        nombre_usuario: detalle.nombre_usuario || '-'
                    })) : []
                );
                
                const mantenimientosFiltrados = dataMantenimientos.filter(
                    (m: any) => m.cve_producto_principal === cve_producto
                );

                setProductoMantenimientos(mantenimientosFiltrados);

                const entradasFiltradas = entradasProcesadas.filter(
                    (d: any) => d.cve_producto === cve_producto
                );

                const salidasFiltradas = salidasProcesadas.filter(
                    (d: any) => d.cve_producto === cve_producto
                );

                setProductoEntradas(entradasFiltradas);
                setProductoSalidas(salidasFiltradas);

                const totalE = entradasFiltradas.reduce((sum: number, e: any) => sum + e.cantidad, 0);
                const totalS = salidasFiltradas.reduce((sum: number, s: any) => sum + s.cantidad, 0);

                const totalM = mantenimientosFiltrados.reduce((sum: number, m: any) => {
                    if (Array.isArray(m.mantenimientoDetalle)) {
                        return sum + m.mantenimientoDetalle.reduce((s: number, d: any) => s + d.cantidad, 0);
                    }
                    return sum;
                }, 0);

                setTotalEntradas(totalE);
                setTotalSalidas(totalS);
                setTotalMantenimientos(totalM);
                setSaldoFinal(totalE - totalS);

            } catch (err) {
                console.error('Error al buscar movimientos:', err);
                setError('No se pudieron obtener los movimientos del producto.');
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'No se pudieron obtener los movimientos del producto.',
                    life: 3000
                });
            } finally {
                setLoading(false);
            }
        };

        fetchMovimientos();
    }, [selectedProducto, usuariosCatalogo, departamentosCatalogo]);

    const showMovimientoDetail = (rowData: any, type: 'entrada' | 'salida' | 'mantenimiento') => {
        let fullData;
        if (type === 'entrada') {
            fullData = productoEntradasFull.find(e => e.folio === rowData.folio);
        } else if (type === 'salida') {
            fullData = productoSalidasFull.find(s => s.folio === rowData.folio);
        } else {
            fullData = productoMantenimientosFull.find(m => m.folio === rowData.folio);
        }

        if (fullData) {
            setSelectedMovimiento(fullData);
            setMovimientoType(type);
            setIsModalVisible(true);
        }
    };

    const exportToExcel = () => {
        if (!selectedMovimiento) return;

        if (movimientoType === 'mantenimiento') {
            const encabezado = [
                ['Folio', selectedMovimiento.folio],
                ['Fecha', formatDate(selectedMovimiento.fecha)],
                ['Observaciones', selectedMovimiento.observaciones || '-'],
                ['Producto Principal', getDescripcionProducto(selectedMovimiento.cve_producto_principal)],
                ['Técnico', getNombreTecnico(selectedMovimiento.id_tecnico)]
            ];

            const detalle = (selectedMovimiento.mantenimientoDetalle || []).map((detalle: any) => [
                detalle.cve_producto,
                getDescripcionProducto(detalle.cve_producto),
                detalle.cantidad,
                detalle.observaciones || '-'
            ]);

            const ws_data: any[] = [];
            encabezado.forEach(row => ws_data.push(row));
            ws_data.push([]); // Línea vacía
            ws_data.push(['Detalle:']);
            ws_data.push(['Clave', 'Descripción', 'Cantidad', 'Observación']);
            detalle.forEach((row: any) => ws_data.push(row));

            const worksheet = XLSX.utils.aoa_to_sheet(ws_data);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Mantenimiento');
            const fileName = `mantenimiento_${selectedMovimiento.folio}_${formatDate(selectedMovimiento.fecha)}.xlsx`;
            XLSX.writeFile(workbook, fileName);

        } else if (movimientoType === 'salida') {
            const detalle = selectedMovimiento.salidaDetalle || [];

            const excelData = detalle.map((detalle: any) => ({
                Clave: detalle.cve_producto,
                Descripción: getDescripcionProducto(detalle.cve_producto),
                Cantidad: detalle.cantidad,
                Sucursal: getNombreSucursal(detalle.id_sucursal_destino) || '-',
                Departamento: getNombreDepartamento(detalle.cve_departamento_destino) || '-',
                Usuario: detalle.nombre_usuario || '-'
            }));

            const worksheet = XLSX.utils.json_to_sheet(excelData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Datos');
            const fileName = `detalles_salida_${selectedMovimiento.folio}_${formatDate(selectedMovimiento.fecha)}.xlsx`;
            XLSX.writeFile(workbook, fileName);

        } else {
            const detalle = selectedMovimiento.entradaDetalle || [];

            const excelData = detalle.map((detalle: any) => ({
                Clave: detalle.cve_producto,
                Descripción: getDescripcionProducto(detalle.cve_producto),
                Cantidad: detalle.cantidad
            }));

            const worksheet = XLSX.utils.json_to_sheet(excelData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Datos');
            const fileName = `detalles_entrada_${selectedMovimiento.folio}_${formatDate(selectedMovimiento.fecha)}.xlsx`;
            XLSX.writeFile(workbook, fileName);
        }
    };

    const exportToPdf = () => {
        if (!selectedMovimiento) return;

        const doc = new jsPDF('l', 'pt', 'a4');
        const img = new Image();
        img.src = '/dist/img/bbtt2.png';
        img.onload = function () {
            doc.addImage(img, 'PNG', 40, 20, 65, 65);

            doc.setProperties({
                title: 'Reporte de Saldos',
                subject: `Detalles de ${movimientoType.charAt(0).toUpperCase() + movimientoType.slice(1)}`,
                author: 'Sistema de Inventarios',
                keywords: 'saldos, reporte, pdf',
                creator: 'Sistema de Inventarios'
            });

            doc.setFontSize(18);
            doc.setTextColor(40);
            doc.text(`Detalles de ${movimientoType.charAt(0).toUpperCase() + movimientoType.slice(1)}`, 150, 40);
            doc.setFontSize(10);
            doc.setTextColor(100);
            doc.text(`Generado el: ${new Date().toLocaleDateString('es-ES')}`, 150, 60);

            let y = 100;

            if (movimientoType === 'mantenimiento') {
                doc.setFontSize(12);
                doc.text(`Folio:`, 40, y);
                doc.setFont('helvetica', 'bold');
                doc.text(`${selectedMovimiento.folio}`, 75, y);
                doc.setFont('helvetica', 'normal');
                y += 18;
                doc.text(`Fecha:`, 40, y);
                doc.setFont('helvetica', 'bold');
                doc.text(`${formatDate(selectedMovimiento.fecha)}`, 85, y);
                doc.setFont('helvetica', 'normal');
                y += 18;
                doc.text(`Observaciones:`, 40, y);
                doc.setFont('helvetica', 'bold');
                doc.text(`${selectedMovimiento.observaciones || '-'}`, 130, y);
                doc.setFont('helvetica', 'normal');
                y += 18;
                doc.text(`Producto Principal:`, 40, y);
                doc.setFont('helvetica', 'bold');
                doc.text(`${getDescripcionProducto(selectedMovimiento.cve_producto_principal)}`, 145, y);
                doc.setFont('helvetica', 'normal');
                y += 18;
                doc.text(`Técnico:`, 40, y);
                doc.setFont('helvetica', 'bold');
                doc.text(`${getNombreTecnico(selectedMovimiento.id_tecnico)}`, 90, y);
                doc.setFont('helvetica', 'normal');
                y += 18;

                const detalle = selectedMovimiento.mantenimientoDetalle || [];
                const pdfData = detalle.map((detalle: any) => [
                    detalle.cve_producto,
                    getDescripcionProducto(detalle.cve_producto),
                    detalle.cantidad,
                    detalle.observaciones || '-'
                ]);

                const autoTableResult = autoTable(doc, {
                    head: [['Clave', 'Descripción', 'Cantidad', 'Observación']],
                    body: pdfData,
                    startY: y + 20,
                    margin: { horizontal: 40 },
                    styles: {
                        fontSize: 8,
                        cellPadding: 3,
                        overflow: 'linebreak',
                        valign: 'middle'
                    },
                    headStyles: {
                        fillColor: [41, 128, 185],
                        textColor: 255,
                        fontStyle: 'bold',
                        halign: 'center',
                        fontSize: 9
                    },
                    bodyStyles: {
                        halign: 'center',
                        valign: 'middle'
                    },
                    columnStyles: {
                        0: { cellWidth: 150, halign: 'center' },
                        1: { cellWidth: 180, halign: 'left' },
                        2: { cellWidth: 120, halign: 'center' },
                        3: { cellWidth: 180, halign: 'left' }
                    },
                    didDrawPage: function (data: any) {
                        const pageCount = doc.getNumberOfPages();
                        const currentPage = data.pageNumber;
                        doc.setFontSize(10);
                        doc.setTextColor(150);
                        doc.text(`Página ${currentPage} de ${pageCount}`, data.settings.margin.left, doc.internal.pageSize.height - 20);
                    }
                });

                let finalY = (autoTableResult && autoTableResult.finalY) || (y + 290);
                doc.setFontSize(12);
                doc.setDrawColor(100);
                doc.line(300, finalY + 40, 550, finalY + 40);
                doc.text('Firma de conformidad', 425, finalY + 60, { align: 'center' });
                doc.save(`mantenimiento_${selectedMovimiento.folio}_${formatDate(selectedMovimiento.fecha)}.pdf`);

            } else if (movimientoType === 'salida') {
                doc.setFontSize(12);
                doc.text(`Folio:`, 40, y);
                doc.setFont('helvetica', 'bold');
                doc.text(`${selectedMovimiento.folio}`, 75, y);
                doc.setFont('helvetica', 'normal');
                y += 18;
                doc.text(`Fecha:`, 40, y);
                doc.setFont('helvetica', 'bold');
                doc.text(`${formatDate(selectedMovimiento.fecha)}`, 85, y);
                doc.setFont('helvetica', 'normal');
                y += 18;
                doc.text(`Observaciones:`, 40, y);
                doc.setFont('helvetica', 'bold');
                doc.text(`${selectedMovimiento.observaciones || '-'}`, 130, y);
                doc.setFont('helvetica', 'normal');
                y += 18;
                doc.text(`Sucursal Destino:`, 40, y);
                doc.setFont('helvetica', 'bold');
                // Mostrar todos los nombres de sucursales destino
                const detalles = Array.isArray(selectedMovimiento.salidaDetalle) ? selectedMovimiento.salidaDetalle : [];
                const sucursales = detalles.length > 0
                    ? Array.from(new Set(detalles.map((d: any) => getNombreSucursal(d.id_sucursal_destino)))).join(', ')
                    : '-';
                doc.text(`${sucursales}`, 160, y);
                doc.setFont('helvetica', 'normal');
                y += 18;
                doc.text(`Departamento Destino:`, 40, y);
                doc.setFont('helvetica', 'bold');
                const departamentos = detalles.length > 0
                    ? Array.from(new Set(detalles.map((d: any) => getNombreDepartamento(d.cve_departamento_destino)))).join(', ')
                    : '-';
                doc.text(`${departamentos}`, 180, y);
                doc.setFont('helvetica', 'normal');
                y += 18;

                const tableData = selectedMovimiento.salidaDetalle || [];
                const pdfData = tableData.map((detalle: any) => [
                    detalle.cve_producto,
                    getDescripcionProducto(detalle.cve_producto),
                    detalle.cantidad,
                    getNombreSucursal(detalle.id_sucursal_destino) || '-',
                    getNombreDepartamento(detalle.cve_departamento_destino) || '-',
                    detalle.nombre_usuario || '-'
                ]);

                autoTable(doc, {
                    head: [['Clave', 'Descripción', 'Cant.', 'Sucursal', 'Departamento', 'Usuario']],
                    body: pdfData,
                    startY: y + 20,
                    margin: { horizontal: 40 },
                    styles: {
                        fontSize: 7,
                        cellPadding: 2,
                        overflow: 'linebreak',
                        valign: 'middle'
                    },
                    headStyles: {
                        fillColor: [41, 128, 185],
                        textColor: 255,
                        fontStyle: 'bold',
                        halign: 'center',
                        fontSize: 8
                    },
                    bodyStyles: {
                        halign: 'center',
                        valign: 'middle'
                    },
                    columnStyles: {
                        0: { cellWidth: 80, halign: 'center' },
                        1: { cellWidth: 200, halign: 'left' },
                        2: { cellWidth: 50, halign: 'center' },
                        3: { cellWidth: 100, halign: 'center' },
                        4: { cellWidth: 100, halign: 'center' },
                        5: { cellWidth: 100, halign: 'center' }
                    },
                    didDrawPage: function (data: any) {
                        const pageCount = doc.getNumberOfPages();
                        const currentPage = data.pageNumber;
                        doc.setFontSize(10);
                        doc.setTextColor(150);
                        doc.text(`Página ${currentPage} de ${pageCount}`, data.settings.margin.left, doc.internal.pageSize.height - 20);
                    }
                });

                doc.save(`detalles_salida_${selectedMovimiento.folio}_${formatDate(selectedMovimiento.fecha)}.pdf`);
            } else {
                // Para entradas, agregar la referencia en el PDF
                doc.setFontSize(12);
                doc.text(`Folio:`, 40, y);
                doc.setFont('helvetica', 'bold');
                doc.text(`${selectedMovimiento.folio}`, 75, y);
                doc.setFont('helvetica', 'normal');
                y += 18;
                doc.text(`Fecha:`, 40, y);
                doc.setFont('helvetica', 'bold');
                doc.text(`${formatDate(selectedMovimiento.fecha)}`, 85, y);
                doc.setFont('helvetica', 'normal');
                y += 18;
                doc.text(`Referencia:`, 40, y);
                doc.setFont('helvetica', 'bold');
                doc.text(`${selectedMovimiento.referencia || '-'}`, 106, y);
                doc.setFont('helvetica', 'normal');
                y += 18;
                doc.text(`Observaciones:`, 40, y);
                doc.setFont('helvetica', 'bold');
                doc.text(`${selectedMovimiento.observaciones || '-'}`, 130, y);
                doc.setFont('helvetica', 'normal');
                y += 18;

                const tableData = selectedMovimiento.entradaDetalle || [];
                const pdfData = tableData.map((detalle: any) => [
                    detalle.cve_producto,
                    getDescripcionProducto(detalle.cve_producto),
                    detalle.cantidad
                ]);

                autoTable(doc, {
                    head: [['Clave', 'Descripción', 'Cantidad']],
                    body: pdfData,
                    startY: y + 20,
                    margin: { horizontal: 40 },
                    styles: {
                        fontSize: 8,
                        cellPadding: 3,
                        overflow: 'linebreak',
                        valign: 'middle'
                    },
                    headStyles: {
                        fillColor: [41, 128, 185],
                        textColor: 255,
                        fontStyle: 'bold',
                        halign: 'center',
                        fontSize: 9
                    },
                    bodyStyles: {
                        halign: 'center',
                        valign: 'middle'
                    },
                    columnStyles: {
                        0: { cellWidth: 180 },
                        1: { cellWidth: 300 },
                        2: { cellWidth: 100 }
                    },
                    didDrawPage: function (data: any) {
                        const pageCount = doc.getNumberOfPages();
                        const currentPage = data.pageNumber;
                        doc.setFontSize(10);
                        doc.setTextColor(150);
                        doc.text(`Página ${currentPage} de ${pageCount}`, data.settings.margin.left, doc.internal.pageSize.height - 20);
                    }
                });

                doc.save(`detalles_entrada_${selectedMovimiento.folio}_${formatDate(selectedMovimiento.fecha)}.pdf`);
            }
        };
    };

    // Template para mostrar la cantidad centrada
    const cantidadBodyTemplate = (rowData: any) => {
        return (
            <div style={{ textAlign: 'center' }}>
                <Badge value={rowData.cantidad} severity="info" />
            </div>
        );
    };

    return (
        <div>
            <Toast ref={toast} />
            <Navbar />
            <Sidebar />
            
            <div className="content-wrapper" style={{
                backgroundImage: 'url(/dist/img/FondoLogin.png)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundAttachment: 'fixed',
                minHeight: '100vh',
                position: 'relative',
                padding: '20px'
            }}>
                
                {/* Header principal mejorado */}
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '16px',
                    padding: '24px',
                    marginBottom: '20px',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <h1 style={{ 
                            margin: 0, 
                            color: '#1f2937', 
                            fontSize: '2rem', 
                            fontWeight: '700',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px'
                        }}>
                            📊 Consulta de Saldos
                        </h1>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '8px 16px',
                            backgroundColor: '#3b82f6',
                            color: 'white',
                            borderRadius: '20px',
                            fontSize: '0.85rem',
                            fontWeight: '600'
                        }}>
                            <i className="pi pi-chart-bar"></i>
                            {loading ? 'CALCULANDO...' : 'SALDOS LISTOS'}
                        </div>
                    </div>
                    <div style={{ height: '2px', background: 'linear-gradient(90deg, #3b82f6, #10b981)', borderRadius: '1px' }}></div>
                </div>

                {/* Panel de selección de producto */}
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '12px',
                    padding: '20px',
                    marginBottom: '20px',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                    <h3 style={{ 
                        margin: '0 0 16px 0', 
                        color: '#374151', 
                        fontSize: '1.2rem', 
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}>
                        📦 Seleccionar Producto
                    </h3>
                    
                    {/* Buscador de productos */}
                    <div style={{ marginBottom: '16px' }}>
                        <label style={{ 
                            display: 'block', 
                            fontSize: '0.9rem', 
                            fontWeight: '600',
                            marginBottom: '6px',
                            color: '#374151'
                        }}>
                            🔍 Buscar Producto
                        </label>
                        <div style={{ position: 'relative' }}>
                            <i className="pi pi-search" style={{
                                position: 'absolute',
                                left: '12px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: '#9ca3af',
                                fontSize: '0.9rem'
                            }}></i>
                            <InputText
                                placeholder="Buscar por clave, descripción o modelo..."
                                value={globalFilterValue}
                                onChange={onGlobalFilterChange}
                                style={{ 
                                    width: '100%', 
                                    height: '40px',
                                    paddingLeft: '40px',
                                    border: '2px solid #e5e7eb',
                                    borderRadius: '8px'
                                }}
                            />
                        </div>
                    </div>

                    {/* Tabla de productos */}
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        border: '1px solid #e5e7eb'
                    }}>
                        <DataTable
                            value={productosCatalogo}
                            globalFilter={globalFilterValue}
                            responsiveLayout="scroll"
                            stripedRows
                            size="small"
                            emptyMessage="📭 No hay productos disponibles"
                            scrollable
                            scrollHeight="300px"
                            rowClassName={productRowClass}
                            selectionMode="single"
                            onRowClick={(e) => onProductoSelect(e.data as InventarioItem)}
                            globalFilterFields={['cve_producto', 'descripcion', 'modelo']}
                            rowHover
                            style={{ fontSize: '0.875rem' }}
                        >
                            <Column 
                                field="cve_producto" 
                                header="🔑 Clave" 
                                style={{ width: '120px', fontWeight: 'bold', textAlign: 'center' }}
                                sortable
                            />
                            <Column 
                                field="descripcion" 
                                header="📝 Descripción" 
                                style={{ minWidth: '300px' }}
                                sortable
                            />
                            <Column 
                                field="modelo" 
                                header="🏷️ Modelo" 
                                style={{ width: '150px', textAlign: 'center' }}
                                sortable
                            />
                        </DataTable>
                    </div>
                </div>

                {/* Sección de resultados con ref para scroll automático */}
                <div ref={resultadosRef}>
                    {loading && (
                        <div style={{
                            backgroundColor: 'rgba(59, 130, 246, 0.1)',
                            padding: '20px',
                            borderRadius: '12px',
                            marginBottom: '20px',
                            border: '1px solid rgba(59, 130, 246, 0.2)',
                            textAlign: 'center'
                        }}>
                            <div style={{ fontSize: '1.5rem', marginBottom: '8px' }}>⏳</div>
                            <strong style={{ color: '#ffffffff' }}>Cargando movimientos...</strong>
                        </div>
                    )}
                    
                    {error && (
                        <div style={{
                            backgroundColor: 'rgba(239, 68, 68, 0.1)',
                            padding: '16px',
                            borderRadius: '8px',
                            marginBottom: '20px',
                            border: '1px solid rgba(239, 68, 68, 0.2)',
                            color: '#dc2626',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <i className="pi pi-exclamation-triangle"></i>
                            {error}
                        </div>
                    )}
                    
                    {selectedProducto && !loading && !error && (
                        <div style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            borderRadius: '12px',
                            padding: '24px',
                            marginBottom: '20px',
                            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                            border: '1px solid rgba(255, 255, 255, 0.2)'
                        }}>
                            <h3 style={{ 
                                margin: '0 0 20px 0', 
                                color: '#374151', 
                                fontSize: '1.3rem', 
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}>
                                📈 Resumen de Saldos: <span style={{ color: '#3b82f6' }}>{selectedProducto.descripcion}</span>
                            </h3>
                            
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                gap: '16px'
                            }}>
                                <div style={{
                                    backgroundColor: '#dcfce7',
                                    padding: '20px',
                                    borderRadius: '12px',
                                    textAlign: 'center',
                                    border: '2px solid #16a34a'
                                }}>
                                    <div style={{ fontSize: '2rem', marginBottom: '8px' }}>📈</div>
                                    <div style={{ fontSize: '0.9rem', color: '#166534', fontWeight: '600', marginBottom: '4px' }}>ENTRADAS</div>
                                    <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#16a34a' }}>{totalEntradas}</div>
                                </div>
                                
                                <div style={{
                                    backgroundColor: '#fef3c7',
                                    padding: '20px',
                                    borderRadius: '12px',
                                    textAlign: 'center',
                                    border: '2px solid #f59e0b'
                                }}>
                                    <div style={{ fontSize: '2rem', marginBottom: '8px' }}>📉</div>
                                    <div style={{ fontSize: '0.9rem', color: '#92400e', fontWeight: '600', marginBottom: '4px' }}>SALIDAS</div>
                                    <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#f59e0b' }}>{totalSalidas}</div>
                                </div>
                                
                                <div style={{
                                    backgroundColor: '#dbeafe',
                                    padding: '20px',
                                    borderRadius: '12px',
                                    textAlign: 'center',
                                    border: '2px solid #3b82f6'
                                }}>
                                    <div style={{ fontSize: '2rem', marginBottom: '8px' }}>📊</div>
                                    <div style={{ fontSize: '0.9rem', color: '#1e40af', fontWeight: '600', marginBottom: '4px' }}>STOCK ACTUAL</div>
                                    <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#3b82f6' }}>{saldoFinal}</div>
                                </div>
                                
                                <div style={{
                                    backgroundColor: '#fce7f3',
                                    padding: '20px',
                                    borderRadius: '12px',
                                    textAlign: 'center',
                                    border: '2px solid #ec4899'
                                }}>
                                    <div style={{ fontSize: '2rem', marginBottom: '8px' }}>🔧</div>
                                    <div style={{ fontSize: '0.9rem', color: '#be185d', fontWeight: '600', marginBottom: '4px' }}>MANTENIMIENTOS</div>
                                    <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#ec4899' }}>{totalMantenimientos}</div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Sección de Entradas */}
                {productoEntradas.length > 0 ? (
                    <div style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        borderRadius: '12px',
                        padding: '20px',
                        marginBottom: '20px',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                    }}>
                        <h3 style={{ 
                            margin: '0 0 16px 0', 
                            color: '#374151', 
                            fontSize: '1.2rem', 
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            📈 Entradas
                        </h3>
                        
                        <div style={{
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            border: '1px solid #e5e7eb'
                        }}>
                            <DataTable
                                value={productoEntradas}
                                responsiveLayout="scroll"
                                stripedRows
                                size="small"
                                emptyMessage="📭 No hay entradas registradas"
                                style={{ fontSize: '0.875rem' }}
                                onRowClick={(e) => {
                                    if (isMovimientoDetalle(e.data)) {
                                        showMovimientoDetail(e.data, 'entrada');
                                    }
                                }}
                                rowHover
                            >
                                <Column field="folio" header="📄 Folio" style={{ width: '100px', textAlign: 'center' }} />
                                <Column
                                    field="fecha"
                                    header="📅 Fecha"
                                    body={(rowData) => formatDate(rowData.fecha)}
                                    style={{ width: '120px', textAlign: 'center' }}
                                />
                                <Column 
                                    field="referencia" 
                                    header="🔗 Referencia" 
                                    body={(rowData) => rowData.referencia || '-'}
                                    style={{ width: '150px' }}
                                />
                                <Column field="observaciones" header="📝 Observación" />
                            </DataTable>
                        </div>
                    </div>
                ) : selectedProducto && !loading ? (
                    <div style={{ 
                        backgroundColor: 'rgba(0, 0, 0, 0.1)', 
                        padding: '16px', 
                        borderRadius: '8px', 
                        marginBottom: '20px',
                        border: '1px solid rgba(0, 0, 0, 0.94)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                    }}>
                        <div style={{ fontSize: '1.5rem' }}>📭</div>
                        <div>
                            <strong style={{ color: '#1e40af' }}>Sin Entradas:</strong>
                            <span style={{ color: '#ffffffff', marginLeft: '8px' }}>
                                No hay entradas registradas para "{selectedProducto.descripcion}"
                            </span>
                        </div>
                    </div>
                ) : null}

                {/* Sección de Salidas */}
                {productoSalidas.length > 0 ? (
                    <div style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        borderRadius: '12px',
                        padding: '20px',
                        marginBottom: '20px',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                    }}>
                        <h3 style={{ 
                            margin: '0 0 16px 0', 
                            color: '#374151', 
                            fontSize: '1.2rem', 
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            📉 Salidas
                        </h3>
                        
                        <div style={{
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            border: '1px solid #e5e7eb'
                        }}>
                            <DataTable
                                value={productoSalidas}
                                responsiveLayout="scroll"
                                stripedRows
                                size="small"
                                emptyMessage="📭 No hay salidas registradas"
                                style={{ fontSize: '0.875rem' }}
                                onRowClick={(e) => {
                                    if (isMovimientoDetalle(e.data)) {
                                        showMovimientoDetail(e.data, 'salida');
                                    }
                                }}
                                rowHover
                            >
                                <Column field="folio" header="📄 Folio" style={{ width: '100px', textAlign: 'center' }} />
                                <Column
                                    field="fecha"
                                    header="📅 Fecha"
                                    body={(rowData) => formatDate(rowData.fecha)}
                                    style={{ width: '120px', textAlign: 'center' }}
                                />
                                <Column field="observaciones" header="📝 Observación" />
                                <Column
                                    field="id_sucursal_destino"
                                    header="🏢 Sucursal Destino"
                                    body={(rowData) => getNombreSucursal(rowData.id_sucursal_destino)}
                                    style={{ textAlign: 'center' }}
                                />
                                <Column
                                    field="cve_departamento_destino"
                                    header="🏬 Departamento Destino"
                                    body={(rowData) => getNombreDepartamento(rowData.cve_departamento_destino)}
                                    style={{ textAlign: 'center' }}
                                />
                                <Column
                                    field="nombre_usuario"
                                    header="👤 Usuario"
                                    body={(rowData) => rowData.nombre_usuario || '-'}
                                    style={{ textAlign: 'center' }}
                                />
                            </DataTable>
                        </div>
                    </div>
                ) : selectedProducto && !loading ? (
                    <div style={{ 
                        backgroundColor: 'rgba(0, 0, 0, 0.1)', 
                        padding: '16px', 
                        borderRadius: '8px', 
                        marginBottom: '20px',
                        border: '1px solid rgba(0, 0, 0, 1)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                    }}>
                        <div style={{ fontSize: '1.5rem' }}>📭</div>
                        <div>
                            <strong style={{ color: '#1e40af' }}>Sin Salidas:</strong>
                            <span style={{ color: '#ffffffff', marginLeft: '8px' }}>
                                No hay salidas registradas para "{selectedProducto.descripcion}"
                            </span>
                        </div>
                    </div>
                ) : null}

                {/* Sección de Mantenimientos */}
                {productoMantenimientos.length > 0 ? (
                    <div style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        borderRadius: '12px',
                        padding: '20px',
                        marginBottom: '20px',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                    }}>
                        <h3 style={{ 
                            margin: '0 0 16px 0', 
                            color: '#374151', 
                            fontSize: '1.2rem', 
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            🔧 Mantenimientos
                        </h3>
                        
                        <div style={{
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            border: '1px solid #e5e7eb'
                        }}>
                            <DataTable
                                value={productoMantenimientos}
                                responsiveLayout="scroll"
                                stripedRows
                                size="small"
                                emptyMessage="📭 No hay mantenimientos registrados"
                                style={{ fontSize: '0.875rem' }}
                                onRowClick={(e) => {
                                    showMovimientoDetail(e.data, 'mantenimiento');
                                }}
                                rowHover
                            >
                                <Column field="folio" header="📄 Folio" style={{ width: '100px', textAlign: 'center' }} />
                                <Column
                                    field="fecha"
                                    header="📅 Fecha"
                                    body={(rowData) => formatDate(rowData.fecha)}
                                    style={{ width: '120px', textAlign: 'center' }}
                                />
                                <Column field="observaciones" header="📝 Observación" />
                            </DataTable>
                        </div>
                    </div>
                ) : selectedProducto && !loading ? (
                    <div style={{ 
                        backgroundColor: 'rgba(7, 7, 7, 0.1)', 
                        padding: '16px', 
                        borderRadius: '8px', 
                        marginBottom: '20px',
                        border: '1px solid rgba(0, 0, 0, 1)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                    }}>
                        <div style={{ fontSize: '1.5rem' }}>📭</div>
                        <div>
                            <strong style={{ color: '#1e40af' }}>Sin mantenimientos:</strong>
                            <span style={{ color: '#ffffffff', marginLeft: '8px' }}>
                                No hay mantenimientos registrados para "{selectedProducto.descripcion}"
                            </span>
                        </div>
                    </div>
                ) : null}
            </div>

            {/* Modal de detalles mejorado */}
            <Dialog
                header={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {movimientoType === 'entrada' ? '📈 Detalles de Entrada' :
                            movimientoType === 'salida' ? '📉 Detalles de Salida' :
                                '🔧 Detalles de Mantenimiento'}
                    </div>
                }
                visible={isModalVisible}
                style={{ width: '90%' }}
                onHide={() => setIsModalVisible(false)}
            >
                {selectedMovimiento && (
                    <div style={{
                        backgroundColor: '#f9fafb',
                        borderRadius: '8px',
                        padding: '20px'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h4 style={{ margin: 0, color: '#374151' }}>
                                {movimientoType === 'entrada' ? '📈 Encabezado de Entrada' :
                                    movimientoType === 'salida' ? '📉 Encabezado de Salida' :
                                        '🔧 Encabezado de Mantenimiento'}
                            </h4>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <Button
                                    icon="pi pi-file-excel"
                                    label="Excel"
                                    size="small"
                                    severity="success"
                                    onClick={exportToExcel}
                                    style={{ borderRadius: '8px' }}
                                />
                                <Button
                                    icon="pi pi-file-pdf"
                                    label="PDF"
                                    size="small"
                                    severity="danger"
                                    onClick={exportToPdf}
                                    style={{ borderRadius: '8px' }}
                                />
                            </div>
                        </div>
                        
                        <div style={{
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            marginBottom: '20px',
                            border: '1px solid #1e489bff'
                        }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <tbody>
                                    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                                        <th style={{ padding: '12px', backgroundColor: '#f3f4f6', fontWeight: '600', width: '150px' }}>📄 Folio</th>
                                        <td style={{ padding: '12px' }}>{selectedMovimiento.folio}</td>
                                    </tr>
                                    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                                        <th style={{ padding: '12px', backgroundColor: '#f3f4f6', fontWeight: '600' }}>📅 Fecha</th>
                                        <td style={{ padding: '12px' }}>{formatDate(selectedMovimiento.fecha)}</td>
                                    </tr>
                                    {movimientoType === 'entrada' && (
                                        <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                                            <th style={{ padding: '12px', backgroundColor: '#f3f4f6', fontWeight: '600' }}>🔗 Referencia</th>
                                            <td style={{ padding: '12px' }}>{selectedMovimiento.referencia || '-'}</td>
                                        </tr>
                                    )}
                                    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                                        <th style={{ padding: '12px', backgroundColor: '#f3f4f6', fontWeight: '600' }}>📝 Observaciones</th>
                                        <td style={{ padding: '12px' }}>{selectedMovimiento.observaciones || '-'}</td>
                                    </tr>
                                    {movimientoType === 'salida' && (() => {
                                        const detalles = Array.isArray(selectedMovimiento.salidaDetalle) ? selectedMovimiento.salidaDetalle : [];
                                        const sucursales = detalles.length > 0
                                            ? Array.from(new Set(detalles.map((d: any) => getNombreSucursal(d.id_sucursal_destino)))).join(', ')
                                            : '-';
                                        const departamentos = detalles.length > 0
                                            ? Array.from(new Set(detalles.map((d: any) => getNombreDepartamento(d.cve_departamento_destino)))).join(', ')
                                            : '-';
                                        return [
                                            <tr key="sucursal" style={{ borderBottom: '1px solid #e5e7eb' }}>
                                                <th style={{ padding: '12px', backgroundColor: '#f3f4f6', fontWeight: '600' }}>🏢 Sucursal Destino</th>
                                                <td style={{ padding: '12px' }}>{sucursales}</td>
                                            </tr>,
                                            <tr key="departamento" style={{ borderBottom: '1px solid #e5e7eb' }}>
                                                <th style={{ padding: '12px', backgroundColor: '#f3f4f6', fontWeight: '600' }}>🏬 Departamento Destino</th>
                                                <td style={{ padding: '12px' }}>{departamentos}</td>
                                            </tr>
                                        ];
                                    })()}
                                    {movimientoType === 'mantenimiento' && (
                                        <>
                                            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                                                <th style={{ padding: '12px', backgroundColor: '#f3f4f6', fontWeight: '600' }}>📦 Producto Principal</th>
                                                <td style={{ padding: '12px' }}>{getDescripcionProducto(selectedMovimiento.cve_producto_principal)}</td>
                                            </tr>
                                            <tr>
                                                <th style={{ padding: '12px', backgroundColor: '#f3f4f6', fontWeight: '600' }}>👨‍🔧 Técnico</th>
                                                <td style={{ padding: '12px' }}>{getNombreTecnico(selectedMovimiento.id_tecnico)}</td>
                                            </tr>
                                        </>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <h4 style={{ margin: '0 0 16px 0', color: '#000000ff', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            📋 Detalle
                        </h4>
                        
                        <div style={{ 
                            maxHeight: '400px', 
                            overflowY: 'auto', 
                            overflowX: 'auto',
                            color: '#000000ff',
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            border: '1px solid #1806b8ff'
                        }}>
                            <table style={{ 
                                width: '100%', 
                                borderCollapse: 'collapse',
                                fontSize: '0.875rem'
                            }}>
                                <thead style={{ 
                                    position: 'sticky', 
                                    top: 0, 
                                    zIndex: 10,
                                    backgroundColor: '#374151',
                                    color: 'white'
                                }}>
                                    <tr>
                                        <th style={{ minWidth: '100px',color: '#000000ff', textAlign: 'center', padding: '12px' }}>🔑 Clave</th>
                                        <th style={{ minWidth: '200px',color: '#000000ff', textAlign: 'center', padding: '12px' }}>📝 Descripción</th>
                                        <th style={{ minWidth: '60px', width: '60px',color: '#000000ff', textAlign: 'center', padding: '12px' }}>📊 Cant.</th>
                                        {movimientoType === 'mantenimiento' && (
                                            <th style={{ minWidth: '150px', textAlign: 'center', padding: '12px' }}>📝 Observación</th>
                                        )}
                                        {movimientoType === 'salida' && (
                                            <>
                                                <th style={{ minWidth: '90px',color: '#000000ff', width: '90px', textAlign: 'center', padding: '12px' }}>🏢 Sucursal</th>
                                                <th style={{ minWidth: '130px', color: '#000000ff',textAlign: 'center', padding: '12px' }}>🏬 Departamento</th>
                                                <th style={{ minWidth: '120px',color: '#000000ff', textAlign: 'center', padding: '12px' }}>👤 Usuario</th>
                                            </>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {(() => {
                                        let detalleArray = [];
                                        if (movimientoType === 'entrada') {
                                            detalleArray = selectedMovimiento.entradaDetalle || [];
                                        } else if (movimientoType === 'salida') {
                                            detalleArray = selectedMovimiento.salidaDetalle || [];
                                        } else {
                                            detalleArray = selectedMovimiento.mantenimientoDetalle || [];
                                        }
                                        
                                        if (detalleArray.length === 0) {
                                            const colSpan = movimientoType === 'entrada' ? 3 : 
                                                          movimientoType === 'salida' ? 6 : 4;
                                            return (
                                                <tr>
                                                    <td colSpan={colSpan} style={{ 
                                                        textAlign: 'center', 
                                                        padding: '20px', 
                                                        fontStyle: 'italic', 
                                                        color: '#354452ff',
                                                        backgroundColor: '#f8f9fa'
                                                    }}>
                                                        📭 No hay detalles disponibles
                                                    </td>
                                                </tr>
                                            );
                                        }
                                        
                                        return detalleArray.map((detalle: any, index: number) => (
                                            <tr key={index} style={{
                                    transition: 'background-color 0.2s',
                                    borderBottom: '1px solid #e5e7eb'
                                }}>
                                    <td style={{
                                        textAlign: 'center',
                                        verticalAlign: 'middle',
                                        fontWeight: '500',
                                        backgroundColor: '#f8f9fa',
                                        padding: '12px'
                                    }}>
                                        {detalle.cve_producto}
                                    </td>
                                    <td style={{
                                        verticalAlign: 'middle',
                                        paddingLeft: '12px',
                                        wordWrap: 'break-word',
                                        padding: '12px'
                                    }}>
                                        {getDescripcionProducto(detalle.cve_producto)}
                                    </td>
                                    <td style={{
                                        textAlign: 'center',
                                        verticalAlign: 'middle',
                                        fontWeight: 'bold',
                                        color: '#3b82f6',
                                        width: '60px',
                                        padding: '12px'
                                    }}>
                                        <Badge value={detalle.cantidad} severity="info" />
                                    </td>
                                    {movimientoType === 'mantenimiento' && (
                                        <td style={{
                                            verticalAlign: 'middle',
                                            paddingLeft: '12px',
                                            wordWrap: 'break-word',
                                            fontStyle: detalle.observaciones ? 'normal' : 'italic',
                                            color: detalle.observaciones ? 'inherit' : '#6c757d',
                                            padding: '12px'
                                        }}>
                                            {detalle.observaciones || '-'}
                                        </td>
                                    )}
                                    {movimientoType === 'salida' && (
                                        <>
                                            <td style={{
                                                textAlign: 'center',
                                                verticalAlign: 'middle',
                                                wordWrap: 'break-word',
                                                width: '90px',
                                                fontSize: '0.8rem',
                                                padding: '12px'
                                            }}>
                                                {getNombreSucursal(detalle.id_sucursal_destino) || '-'}
                                            </td>
                                            <td style={{
                                                textAlign: 'center',
                                                verticalAlign: 'middle',
                                                wordWrap: 'break-word',
                                                fontSize: '0.85rem',
                                                padding: '12px'
                                            }}>
                                                {getNombreDepartamento(detalle.cve_departamento_destino) || '-'}
                                            </td>
                                            <td style={{
                                                textAlign: 'center',
                                                verticalAlign: 'middle',
                                                wordWrap: 'break-word',
                                                fontWeight: '500',
                                                color: '#16a34a',
                                                padding: '12px'
                                            }}>
                                                {detalle.nombre_usuario || '-'}
                                            </td>
                                        </>
                                    )}
                                </tr>
                            ));
                        })()}
                    </tbody>
                </table>
            </div>

            {movimientoType === 'mantenimiento' && (
                <div style={{
                    marginTop: '3rem',
                    textAlign: 'center',
                    padding: '20px',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb'
                }}>
                    <div style={{
                        fontSize: '1.5rem',
                        marginBottom: '0.5rem',
                        color: '#374151'
                    }}>
                        _____________________________
                    </div>
                    <div style={{
                        fontWeight: 'bold',
                        letterSpacing: '2px',
                        color: '#374151',
                        fontSize: '1.1rem'
                    }}>
                        ✍️ Firma de conformidad
                    </div>
                </div>
            )}
        </div>
    )}
</Dialog>
</div>
);
};

export default Saldos;